import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Leaf, Globe, TrendingDown, TrendingUp, Factory, Zap,
  BarChart3, Download, RefreshCw, MapPin, Cloud, Sun,
  Wind, Droplets, AlertTriangle, CheckCircle, Target, Link as LinkIcon
} from 'lucide-react';
import { 
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  AreaChart, Area
} from 'recharts';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGION_COLORS = {
  'us-east-1': '#E11D48',
  'us-west-2': '#F59E0B', 
  'eu-west-1': '#059669',
  'eu-north-1': '#10B981',
  'ap-southeast-1': '#0EA5E9'
};

const CarbonCost = () => {
  const { shouldShowData } = useDemoData();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasConnection, setHasConnection] = useState(false);

  useEffect(() => {
    checkConnectionAndFetch();
  }, []);

  const checkConnectionAndFetch = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Check for real cloud connections
      const response = await axios.get(`${API_URL}/carbon/overview`, { headers });
      if (response.data && response.data.summary) {
        setHasConnection(true);
        setData(response.data);
      } else {
        setHasConnection(false);
      }
    } catch (error) {
      console.error('Failed to fetch carbon data:', error);
      setHasConnection(false);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Carbon Cost" description="Connect your cloud account to see real data here." />;
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="carbon-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Card key={i}><CardContent className="p-6"><Skeleton className="h-24" /></CardContent></Card>)}
        </div>
        <Card><CardContent className="p-6"><Skeleton className="h-80" /></CardContent></Card>
      </div>
    );
  }

  // Empty state when no connection (skip for demo user)
  if (!hasConnection && !shouldShowData) {
    return (
      <div className="p-6 space-y-6" data-testid="carbon-empty">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Leaf className="w-7 h-7 text-emerald-500" />
            Carbon + Cost Optimization
          </h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            ESG reporting and sustainable cloud optimization
          </p>
        </div>
        <Card className="border-dashed border-2 border-slate-300 dark:border-slate-600">
          <CardContent className="py-16 text-center">
            <Leaf className="w-16 h-16 mx-auto text-slate-400 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
              Connect Your Cloud Providers
            </h3>
            <p className="text-sm text-slate-400 dark:text-slate-400 mb-6 max-w-md mx-auto">
              Connect your AWS, Azure, or GCP accounts to track carbon emissions, get ESG reports, and discover sustainable optimization opportunities.
            </p>
            <Link to="/cloud-audit">
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="connect-cloud-btn">
                <LinkIcon className="w-4 h-4 mr-2" />
                Connect Cloud Account
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const summary = data?.summary || {};
  const byRegion = data?.by_region || [];
  const byService = data?.by_service || [];
  const trends = data?.trends || [];
  const recommendations = data?.recommendations || [];
  const esgScore = data?.esg_score || {};

  return (
    <div className="p-6 space-y-6" data-testid="carbon-cost">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
            <Leaf className="w-7 h-7 text-emerald-500" />
            Carbon + Cost Optimization
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
            ESG reporting and sustainable cloud optimization
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={checkConnectionAndFetch} className="gap-2" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="export-btn">
            <Download className="w-4 h-4" />
            ESG Report
          </Button>
        </div>
      </div>

      {/* ESG Score Banner — solid deep-emerald with strong white text contrast (v34.43) */}
      <Card className="bg-emerald-700 border-0 shadow-lg shadow-emerald-900/20" data-testid="esg-banner">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-emerald-50/90 text-sm font-semibold tracking-wide uppercase">Your ESG Sustainability Score</p>
              <div className="flex items-end gap-2 mt-1">
                <p className="text-5xl font-extrabold text-white tabular-nums">{esgScore.overall}</p>
                <p className="text-emerald-100 text-lg font-semibold mb-1">/100</p>
              </div>
              <p className="text-emerald-50 text-sm font-medium mt-1">
                {esgScore.overall >= 70 ? 'Good · Above Industry Average' : esgScore.overall >= 50 ? 'Average · Room for Improvement' : 'Needs Attention'}
              </p>
            </div>
            <div className="flex gap-3">
              <div className="bg-emerald-900/40 ring-1 ring-white/20 rounded-lg p-4 text-center min-w-[96px]">
                <Globe className="w-6 h-6 text-white mx-auto mb-1.5" />
                <p className="text-emerald-50 text-[11px] font-semibold uppercase tracking-wider">Environmental</p>
                <p className="text-2xl font-extrabold text-white tabular-nums mt-0.5">{esgScore.environmental}</p>
              </div>
              <div className="bg-emerald-900/40 ring-1 ring-white/20 rounded-lg p-4 text-center min-w-[96px]">
                <Target className="w-6 h-6 text-white mx-auto mb-1.5" />
                <p className="text-emerald-50 text-[11px] font-semibold uppercase tracking-wider">Governance</p>
                <p className="text-2xl font-extrabold text-white tabular-nums mt-0.5">{esgScore.governance}</p>
              </div>
              <div className="bg-emerald-900/40 ring-1 ring-white/20 rounded-lg p-4 text-center min-w-[96px]">
                <BarChart3 className="w-6 h-6 text-white mx-auto mb-1.5" />
                <p className="text-emerald-50 text-[11px] font-semibold uppercase tracking-wider">Tracking</p>
                <p className="text-2xl font-extrabold text-white tabular-nums mt-0.5">{esgScore.tracking}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Green Cloud Badge Certification */}
      {esgScore.overall >= 70 && (
        <Card className="border-2 border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/10" data-testid="green-badge">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
              <Leaf className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-emerald-900 dark:text-emerald-300">Green Cloud Badge Certified</h3>
                <Badge className="bg-emerald-500 text-white text-xs">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              </div>
              <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-0.5">
                Your team has achieved sustainability targets! ESG score {esgScore.overall}/100 is above the 70-point threshold for Green Cloud certification.
                Share this badge on your company profile to showcase your commitment to sustainable cloud computing.
              </p>
            </div>
            <Button variant="outline" size="sm" className="border-emerald-500 text-emerald-700 hover:bg-emerald-100 flex-shrink-0" data-testid="share-badge-btn">
              Share Badge
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="carbon-total">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <Factory className="w-8 h-8 text-slate-400" />
              {summary.month_over_month_change < 0 ? (
                <Badge className="bg-emerald-100 text-emerald-700">{summary.month_over_month_change}%</Badge>
              ) : (
                <Badge className="bg-rose-100 text-rose-700">+{summary.month_over_month_change}%</Badge>
              )}
            </div>
            <p className="text-sm text-slate-400 mt-3">Total Carbon Emissions</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatNumber(summary.total_carbon_kg)} kg</p>
            <p className="text-xs text-slate-400 mt-1">{summary.total_carbon_tons} metric tons CO₂e</p>
          </CardContent>
        </Card>

        <Card data-testid="green-energy">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <Sun className="w-8 h-8 text-amber-500" />
              <Badge className="bg-amber-100 text-amber-700">{summary.green_energy_percent}%</Badge>
            </div>
            <p className="text-sm text-slate-400 mt-3">Renewable Energy</p>
            <p className="text-2xl font-bold text-amber-600">{summary.green_energy_percent}%</p>
            <Progress value={summary.green_energy_percent} className="h-2 mt-2" />
          </CardContent>
        </Card>

        <Card data-testid="carbon-intensity">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <Zap className="w-8 h-8 text-emerald-500" />
            </div>
            <p className="text-sm text-slate-400 mt-3">Carbon Intensity</p>
            <p className="text-2xl font-bold text-emerald-600">{summary.carbon_intensity} kg/$ </p>
            <p className="text-xs text-slate-400 mt-1">CO₂ per dollar spent</p>
          </CardContent>
        </Card>

        <Card data-testid="offset-cost">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <Leaf className="w-8 h-8 text-emerald-500" />
            </div>
            <p className="text-sm text-slate-400 mt-3">Offset Cost (Est.)</p>
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(summary.carbon_offset_cost)}</p>
            <p className="text-xs text-slate-400 mt-1">To achieve carbon neutral</p>
          </CardContent>
        </Card>
      </div>

      {/* Reduction Target Progress */}
      <Card data-testid="reduction-target">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">Carbon Reduction Target</h3>
              <p className="text-sm text-slate-400">Progress towards {summary.target_reduction}% annual reduction goal</p>
            </div>
            <Badge className={summary.current_reduction >= summary.target_reduction ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
              {summary.current_reduction}% / {summary.target_reduction}% target
            </Badge>
          </div>
          <Progress value={(summary.current_reduction / summary.target_reduction) * 100} className="h-4" />
          <div className="flex justify-between mt-2 text-sm text-slate-400">
            <span>0%</span>
            <span>{summary.target_reduction}% Target</span>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="overview" className="gap-2" data-testid="overview-tab">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="regions" className="gap-2" data-testid="regions-tab">
            <MapPin className="w-4 h-4" />
            By Region
          </TabsTrigger>
          <TabsTrigger value="recommendations" className="gap-2" data-testid="recommendations-tab">
            <Leaf className="w-4 h-4" />
            Green Actions
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Carbon Trends */}
            <Card data-testid="carbon-trends">
              <CardHeader>
                <CardTitle className="text-lg">Carbon Emissions Trend</CardTitle>
                <CardDescription>6-month historical trend</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                    <AreaChart data={trends}>
                      <defs>
                        <linearGradient id="colorCarbon" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}t`} />
                      <Tooltip formatter={(value) => [`${formatNumber(value)} kg`, 'Carbon']} />
                      <Area type="monotone" dataKey="carbon" stroke="#059669" fill="url(#colorCarbon)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* By Service */}
            <Card data-testid="by-service">
              <CardHeader>
                <CardTitle className="text-lg">Emissions by Service</CardTitle>
                <CardDescription>Carbon footprint breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                    <PieChart>
                      <Pie
                        data={byService}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={3}
                        dataKey="carbon_kg"
                        nameKey="service"
                      >
                        {byService.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#059669', '#0EA5E9', '#F59E0B', '#8B5CF6', '#64748b'][index % 5]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${formatNumber(value)} kg`, 'Carbon']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Regions Tab */}
        <TabsContent value="regions" className="space-y-6">
          <Card data-testid="region-comparison">
            <CardHeader>
              <CardTitle className="text-lg">Regional Carbon Comparison</CardTitle>
              <CardDescription>Carbon emissions and renewable energy by region</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                  <BarChart data={byRegion}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="region" />
                    <YAxis yAxisId="left" tickFormatter={(v) => `${(v/1000).toFixed(0)}t`} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="carbon_kg" fill="#E11D48" name="Carbon (kg)" />
                    <Bar yAxisId="right" dataKey="renewable_percent" fill="#059669" name="Renewable %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Region Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {byRegion.map((region, idx) => (
              <Card key={idx} className="border-l-4" style={{ borderLeftColor: REGION_COLORS[region.region] || '#64748b' }}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                      <MapPin className="w-4 h-4" style={{ color: REGION_COLORS[region.region] }} />
                      {region.region}
                    </h4>
                    <Badge variant="outline" className={region.renewable_percent >= 50 ? 'border-emerald-500 text-emerald-700' : 'border-amber-500 text-amber-700'}>
                      {region.renewable_percent}% renewable
                    </Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Carbon</span>
                      <span className="font-medium">{formatNumber(region.carbon_kg)} kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Cloud Cost</span>
                      <span className="font-medium">{formatCurrency(region.cost)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Energy Mix</span>
                      <span className="font-medium text-xs">{region.energy_mix}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">PUE</span>
                      <span className={`font-medium ${region.pue <= 1.3 ? 'text-emerald-600' : region.pue <= 1.5 ? 'text-amber-600' : 'text-rose-600'}`}>
                        {region.pue}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-4">
          <div className="space-y-4">
            {recommendations.map((rec, idx) => (
              <Card key={idx} className="hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors" data-testid={`recommendation-${rec.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${rec.cost_impact < 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                        <Leaf className={`w-6 h-6 ${rec.cost_impact < 0 ? 'text-emerald-600' : 'text-amber-600'}`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 dark:text-white">{rec.title}</h4>
                        <p className="text-sm text-slate-400 mt-1">{rec.description}</p>
                        <div className="flex items-center gap-4 mt-3">
                          <Badge variant="outline" className="gap-1">
                            <Factory className="w-3 h-3" />
                            -{formatNumber(rec.carbon_savings)} kg CO₂
                          </Badge>
                          <Badge variant="outline" className={rec.difficulty === 'low' ? 'border-emerald-500' : rec.difficulty === 'medium' ? 'border-amber-500' : 'border-rose-500'}>
                            {rec.difficulty} effort
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-xl font-bold ${rec.cost_impact < 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {rec.cost_impact < 0 ? '-' : '+'}{formatCurrency(Math.abs(rec.cost_impact))}
                      </p>
                      <p className="text-xs text-slate-400">monthly impact</p>
                      <Button size="sm" className="mt-2 gap-1 bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle className="w-3 h-3" />
                        Apply
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Total Impact Summary */}
          <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-emerald-800 dark:text-emerald-200">Total Potential Impact</h4>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">If all recommendations are implemented</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-emerald-600">
                    -{formatNumber(recommendations.reduce((sum, r) => sum + r.carbon_savings, 0))} kg CO₂
                  </p>
                  <p className="text-sm text-emerald-500">
                    {formatCurrency(Math.abs(recommendations.filter(r => r.cost_impact < 0).reduce((sum, r) => sum + r.cost_impact, 0)))} monthly savings
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CarbonCost;
